
import { Link } from 'react-router-dom';
import { rankingCards } from '../../../mocks/ranking-cards';
import { useEffect, useRef, useState } from 'react';

export default function RankingList() {
  const [visibleCards, setVisibleCards] = useState<Set<number>>(new Set());
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    cardRefs.current.forEach((card, index) => {
      if (!card) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setTimeout(() => {
                setVisibleCards((prev) => new Set(prev).add(index));
              }, index * 150);
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: '0px 0px -50px 0px',
        }
      );

      observer.observe(card);
      observers.push(observer);
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, []);

  const getRankStyle = (index: number) => {
    if (index === 0) return 'from-amber-400 to-yellow-500 shadow-amber-500/40';
    if (index === 1) return 'from-gray-300 to-gray-400 shadow-gray-400/40';
    if (index === 2) return 'from-orange-400 to-amber-600 shadow-orange-500/40';
    return 'from-gray-200 to-gray-300 shadow-gray-300/30';
  };

  const getRankIcon = (index: number) => {
    if (index === 0) return 'ri-trophy-fill';
    if (index === 1) return 'ri-medal-fill';
    if (index === 2) return 'ri-award-fill';
    return '';
  };

  return (
    <section id="ranking-section" className="py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* ヘッダー */}
        <div className="text-center mb-10 sm:mb-12 md:mb-16">
          <span className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-700 px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-bold mb-4 sm:mb-6 shadow-sm">
            <i className="ri-trophy-line text-base sm:text-lg"></i>
            人気ランキング
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-5 tracking-tight px-4">
            クレジットカード
            <br className="sm:hidden" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-600">おすすめランキング</span>
          </h2>
          <p className="text-base sm:text-lg text-gray-500 max-w-2xl mx-auto px-4">
            編集部が厳選した人気クレジットカードをランキング形式でご紹介
          </p>
        </div>

        {/* ランキングリスト */}
        <div className="space-y-6 sm:space-y-8">
          {rankingCards.map((card, index) => (
            <div
              key={card.id}
              ref={(el) => {
                cardRefs.current[index] = el;
              }}
              className={`group bg-white rounded-2xl sm:rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-700 overflow-hidden border border-gray-100 hover:border-teal-200 ${
                visibleCards.has(index)
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-12'
              }`}
              style={{
                transition: 'opacity 0.7s ease-out, transform 0.7s ease-out',
              }}
            >
              <div className="p-6 sm:p-8 md:p-10">
                <div className="flex flex-col lg:flex-row gap-6 sm:gap-8">
                  {/* ランクバッジ */}
                  <div className="flex-shrink-0 flex lg:flex-col items-center gap-3 sm:gap-4">
                    <div className={`relative w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br ${getRankStyle(index)} rounded-2xl flex items-center justify-center shadow-lg transform transition-transform duration-500 ${
                      visibleCards.has(index) ? 'scale-100 rotate-0' : 'scale-0 rotate-45'
                    }`}>
                      {index < 3 && (
                        <i className={`${getRankIcon(index)} text-white text-xl sm:text-2xl absolute -top-2 -right-2`}></i>
                      )}
                      <span className="text-white text-2xl sm:text-3xl font-bold">{index + 1}</span>
                    </div>
                    <span className="text-gray-400 text-xs sm:text-sm font-medium lg:hidden">位</span>
                    <span className="text-gray-400 text-sm font-medium hidden lg:block">位</span>
                  </div>

                  {/* カード情報 */}
                  <div className="flex-1">
                    <div className="flex flex-col md:flex-row md:items-start gap-6 sm:gap-8 mb-6 sm:mb-8">
                      {/* ロゴ */}
                      <div className="relative flex-shrink-0 mx-auto md:mx-0">
                        <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-50 rounded-2xl blur-lg opacity-50"></div>
                        <div className="relative w-48 h-28 sm:w-52 sm:h-32 bg-gradient-to-br from-white to-gray-50 rounded-2xl p-4 sm:p-5 flex items-center justify-center border border-gray-100 shadow-sm">
                          <img
                            src={card.logo}
                            alt={card.name}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      </div>

                      {/* 詳細 */}
                      <div className="flex-1">
                        <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-4 sm:mb-5 group-hover:text-teal-600 transition-colors text-center md:text-left">
                          <Link to={card.detailUrl} className="hover:underline cursor-pointer">
                            {card.name}
                          </Link>
                        </h3>
                        
                        {/* スペック */}
                        <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-5 sm:mb-6">
                          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                            <p className="text-xs text-gray-500 mb-1 font-medium">還元率</p>
                            <p className="text-xl sm:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-600">{card.returnRate}</p>
                          </div>
                          <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                            <p className="text-xs text-gray-500 mb-1 font-medium">年会費</p>
                            <p className="text-xl sm:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-600">{card.annualFee}</p>
                          </div>
                        </div>

                        {/* メリット */}
                        <div className="space-y-2">
                          <p className="text-xs sm:text-sm font-bold text-gray-500 mb-3 flex items-center gap-2">
                            <i className="ri-star-line text-amber-500"></i>
                            おすすめポイント
                          </p>
                          {card.benefits.map((benefit, idx) => (
                            <div key={idx} className="flex items-start gap-2 sm:gap-3">
                              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                                <i className="ri-check-line text-white text-xs sm:text-sm"></i>
                              </div>
                              <p className="text-gray-600 text-sm sm:text-base">{benefit}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* CTAボタン */}
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                      <a
                        href={card.officialUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold px-6 sm:px-8 py-4 sm:py-5 rounded-xl sm:rounded-2xl transition-all text-center cursor-pointer whitespace-nowrap shadow-lg shadow-orange-500/25 hover:shadow-xl hover:shadow-orange-500/30 transform hover:scale-[1.02] flex items-center justify-center gap-2 text-sm sm:text-base"
                      >
                        <i className="ri-external-link-line text-lg sm:text-xl"></i>
                        今すぐ申し込む
                      </a>
                      <Link
                        to={card.detailUrl}
                        className="flex-1 bg-white hover:bg-gray-50 text-gray-700 font-bold px-6 sm:px-8 py-4 sm:py-5 rounded-xl sm:rounded-2xl border-2 border-gray-200 hover:border-teal-300 transition-all text-center cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 text-sm sm:text-base"
                      >
                        詳細を見る
                        <i className="ri-arrow-right-line"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
